import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Send, Loader2, MessageCircle, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChatBubble } from '@/components/onboarding/ChatBubble';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { TypewriterMessage } from '@/components/onboarding/TypewriterMessage';
import { createSSELineBuffer, processSSEChunk, flushAndProcessSSE } from '@/lib/sseParser';

// Extract navigation links from AI response
const extractNavigationLinks = (text: string): { cleanText: string; links: { path: string; label: string }[] } => {
  const links: { path: string; label: string }[] = [];
  let cleanText = text;
  
  // Pattern 1: Markdown-style links [Label](/path)
  const markdownPattern = /\[([^\]]+)\]\((\/[\w-]+(?:\/[\w-]*)*)\)/gi;
  let match;
  while ((match = markdownPattern.exec(text)) !== null) {
    const label = match[1].trim();
    const path = match[2];
    if (!links.some(l => l.path === path)) {
      links.push({ path, label });
    }
    cleanText = cleanText.replace(match[0], label);
  }
  
  // Pattern 2: "Access it here: /path" format
  const accessPattern = /Access it here:\s*(\/[\w-]+(?:\/[\w-]*)*)/gi;
  while ((match = accessPattern.exec(text)) !== null) {
    const path = match[1];
    const pathLabel = path.slice(1).split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    if (!links.some(l => l.path === path)) {
      links.push({ path, label: `Go to ${pathLabel}` });
    }
  }
  
  // Pattern 3: Standalone paths
  const standalonePattern = /(?:at|visit|go to|open|try|check out|recommend)\s+(\/[\w-]+(?:\/[\w-]*)*)/gi;
  while ((match = standalonePattern.exec(text)) !== null) {
    const path = match[1];
    const pathLabel = path.slice(1).split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    if (!links.some(l => l.path === path)) {
      links.push({ path, label: `Open ${pathLabel}` });
    }
  }
  
  return { cleanText, links };
};

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  showTherappiButton?: boolean;
  navigationLinks?: { path: string; label: string }[];
  isNew?: boolean; // Flag to enable typewriter for new assistant messages
}

interface WhoopContextData {
  todayRecovery: number | null;
  todayHrv: number | null;
  todaySleep: number | null;
  todayStrain: number | null;
  avgRecovery: number | null;
  avgHrv: number | null;
  avgSleep: number | null;
  trend: 'improving' | 'declining' | 'stable' | null;
  recentDays: Array<{
    date: string;
    recovery: number | null;
    hrv: number | null;
    sleep: number | null;
    strain: number | null;
  }>;
}

interface DailyCheckinContext {
  emotion: string | null;
  sleepQuality: number | null;
  energyLevel: string | null;
  stressLevel: number | null;
}

interface PageContext {
  title: string;
  greeting: string;
  hints: string;
}

const PAGE_CONTEXT_MAP: Record<string, PageContext> = {
  '/': {
    title: 'Today Dashboard',
    greeting: "Welcome to your Today Dashboard - your daily wellness command centre.",
    hints: "• Tap the Daily Actions carousel to complete today's check-in, breathwork, or gratitude prompt.\n• Scroll down to your Wellness Score card to see what's driving today's number.\n• Use the Quick-Access cards to jump straight to Nutrition, Sleep, or Therapy.\nIf you're unsure where to start, just complete the Daily Check-In - everything else builds from there."
  },
  '/dashboard': {
    title: 'Today Dashboard',
    greeting: "Welcome to your Today Dashboard - your daily wellness command centre.",
    hints: "• Tap the Daily Actions carousel to complete today's check-in, breathwork, or gratitude prompt.\n• Scroll down to your Wellness Score card to see what's driving today's number.\n• Use the Quick-Access cards to jump straight to Nutrition, Sleep, or Therapy.\nIf you're unsure where to start, just complete the Daily Check-In - everything else builds from there."
  },
  '/nutrition': {
    title: 'Food & Nutrition',
    greeting: "You're in Food-Mood Intelligence - where every meal tells a story about how you feel.",
    hints: "• Use the Day tab to log a meal right now - tap the + button and choose Photo Scan, Search, or Barcode.\n• Switch to the Calendar tab to see which days you logged food and spot gaps in your streak.\n• Open the Insights tab to see your food-mood correlations and AI-generated patterns.\nIf you're unsure what to log, just describe what you ate in the search box - we'll handle the nutrition analysis."
  },
  '/therapy': {
    title: 'Therappi AI Reflection',
    greeting: "This is your Therappi space - a confidential, judgement-free zone for reflection and support.",
    hints: "• Type or speak your thoughts in the chat input at the bottom - Therappi responds with empathy and depth.\n• Use the Mode Selector (top of screen) to switch between Support, CBT, DBT, or Coaching styles.\n• Tap the microphone icon to switch to Voice Mode for a more natural conversation.\nIf you're unsure what to say, start with 'I've been feeling…' - that's always enough to begin."
  },
  '/sanctuary': {
    title: 'The Sanctuary',
    greeting: "Welcome to The Sanctuary - a calming space for nervous system regulation and grounding.",
    hints: "• Tap any Breathing Exercise card to start a guided breath session (Box, 4-7-8, or Coherence).\n• Use the Grounding Tools section if you're feeling overwhelmed or anxious right now.\n• Press the Emergency Calm button at the top for immediate nervous system support.\nIf you're unsure which tool to use, the Breathing Exercises are always a safe and effective starting point."
  },
  '/insights': {
    title: 'AI Insights',
    greeting: "Welcome to AI Insights - where your data becomes meaningful patterns you can act on.",
    hints: "• Browse the Insight Cards to see AI-generated observations about your recent data.\n• Use the Time Range Selector to zoom in on the last 7, 30, or 90 days.\n• Tap a Correlation card to explore how two metrics (e.g. sleep & mood) relate to each other.\nIf you're unsure what the insights mean, just ask me - I can explain any card in plain language."
  },
  '/settings': {
    title: 'Settings',
    greeting: "Customise your Therappi experience so it works exactly the way you need it to.",
    hints: "• Update your Profile section to personalise your name, photo, and display preferences.\n• Manage Notifications to control which reminders Therappi sends and when.\n• Use Connected Devices to link or unlink WHOOP, Garmin, or other integrations.\nIf you're unsure about a setting, hover over the info icon next to it - or ask me what it does."
  },
  '/wellness-tracks': {
    title: 'Wellness Tracks',
    greeting: "Wellness Tracks are guided multi-week journeys built around specific goals - pick one and follow the path.",
    hints: "• Browse the Track Cards and tap 'Start Track' on whichever matches your current focus.\n• Check your Progress Bars to see how far through your active track you are.\n• Use the Category Filters (Sleep, Anxiety, Energy, etc.) to find tracks relevant to what you're experiencing.\nIf you're unsure which track to pick, tell me what you've been struggling with and I'll recommend one."
  },
  '/breathwork': {
    title: 'Breathwork',
    greeting: "Breathwork is one of the fastest ways to shift your nervous system state - even 5 minutes makes a difference.",
    hints: "• Tap any Exercise Card to start - the animated breath guide will lead you through the pattern.\n• Use the Duration Filter to find a session that fits your available time (2, 5, or 10 minutes).\n• Switch Category Tabs to explore Calm, Energise, Focus, or Sleep breathwork styles.\nIf you're unsure which to choose, Box Breathing is a great all-rounder for any time of day."
  },
  '/meditations': {
    title: 'Meditations',
    greeting: "Find a guided meditation that matches where you are right now - no experience needed.",
    hints: "• Tap any Session Card to preview or play a meditation immediately.\n• Use the Category Filters to narrow by theme (sleep, anxiety, focus, self-compassion).\n• Try the Duration Filter to find sessions from 3 to 30 minutes.\nIf you're unsure where to start, a 5-minute Body Scan is gentle and effective for beginners."
  },
  '/sleep-and-dreams': {
    title: 'Sleep & Dreams',
    greeting: "Sleep & Dreams helps you understand your rest - log your sleep, record your dreams, and spot patterns.",
    hints: "• Tap 'Log Sleep' to record last night's hours, quality, and how you woke up feeling.\n• Use the Dream Journal button to capture any dreams before they fade - even fragments are valuable.\n• Switch to the Analysis tab to see your sleep score trends and AI-generated recommendations.\nIf you're unsure what to log, even a rough estimate of hours and a 1-5 quality rating is enough to build useful data."
  },
  '/whoop-dashboard': {
    title: 'WHOOP Dashboard',
    greeting: "Your WHOOP biometric data, surfaced with AI context so you know what it actually means for your wellness.",
    hints: "• Check today's Recovery Score at the top - green (67%+) means you can push, yellow means moderate, red means rest.\n• Scroll to the HRV Trend chart to see how your nervous system has been recovering over the past 7 days.\n• Tap the AI Insights button to get a personalised interpretation of your latest WHOOP data.\nIf you're unsure what a metric means, ask me - I'll explain Recovery, HRV, Strain, or Sleep Performance in plain terms."
  },
  '/whoop': {
    title: 'WHOOP Connect',
    greeting: "Connect your WHOOP device to automatically sync your biometric data into Therappi.",
    hints: "• Tap the Connect button and log in with your WHOOP account credentials to authorise the link.\n• Check the Sync Status indicator to confirm your data is flowing in correctly.\n• Review Data Permissions to control exactly which WHOOP metrics are shared with Therappi.\nIf you're unsure about privacy, you can disconnect at any time from Settings > Connected Devices."
  },
  '/inflammation': {
    title: 'Inflammation Hub',
    greeting: "The Inflammation Hub helps you track lifestyle factors that affect inflammation - and take action to reduce it.",
    hints: "• Check your Inflammation Score at the top to see your current estimated level based on logged data.\n• Browse Protocol Cards to find evidence-based interventions (cold exposure, anti-inflammatory diet, sleep optimisation).\n• Use the Trigger Log to record foods, stressors, or events that may be spiking your inflammation.\nIf you're unsure where to start, logging your diet and sleep for 3 days gives us enough data for meaningful insights."
  },
  '/longevity': {
    title: 'Longevity & Holistic Health',
    greeting: "Longevity brings together evidence-based protocols and insights for sustainable, long-term wellbeing.",
    hints: "• Review your Longevity Score and tap the breakdown to see which pillars (sleep, movement, nutrition, stress) need attention.\n• Explore Protocol Cards to discover structured interventions backed by research.\n• Use the Biomarker Tracking section to log key health indicators over time.\nIf you're unsure what to prioritise, start with whichever pillar shows the lowest score in your breakdown."
  },
  '/daily-check-in': {
    title: 'Daily Check-In',
    greeting: "The Daily Check-In is the single most powerful habit in Therappi - it takes 2 minutes and unlocks everything else.",
    hints: "• Tap a Mood on the selector to set your emotional baseline for today.\n• Use the Sleep Rating slider to log how rested you feel - this feeds your wellness score.\n• Adjust the Energy and Stress sliders, then tap Submit to save your check-in and earn your streak.\nIf you're unsure how you feel, pick the closest option - there's no wrong answer and you can update it later."
  },
  '/daily-checkin': {
    title: 'Daily Check-In',
    greeting: "The Daily Check-In is the single most powerful habit in Therappi - it takes 2 minutes and unlocks everything else.",
    hints: "• Tap a Mood on the selector to set your emotional baseline for today.\n• Use the Sleep Rating slider to log how rested you feel - this feeds your wellness score.\n• Adjust the Energy and Stress sliders, then tap Submit to save your check-in and earn your streak.\nIf you're unsure how you feel, pick the closest option - there's no wrong answer and you can update it later."
  },
  '/mental-wellness': {
    title: 'Mental Wellness',
    greeting: "Monitor your emotional landscape over time - patterns in mental wellness are where the real insights live.",
    hints: "• Check your Mental Wellness Score at the top and tap it to see the contributing factors.\n• Scroll to the Mood Chart to visualise your emotional ups and downs over the past weeks.\n• Use the Assessment Tools section to take validated measures like PHQ-9 or GAD-7.\nIf you're unsure what's affecting your score, ask me - I can help interpret what the data is saying."
  },
  '/future-self-navigator': {
    title: 'Future Self Navigator',
    greeting: "The Future Self Navigator helps you build a vivid, motivating picture of who you're becoming.",
    hints: "• Start by writing your Future Self Identity Statement - a 1-2 sentence vision of your ideal self.\n• Use the Values Alignment tool to identify which values your future self embodies.\n• Tap 'Write a Letter' to compose a message from your future self back to today - this is deeply powerful.\nIf you're unsure what to write, begin with 'In one year, I want to feel…' - the rest will follow."
  },
  '/future-self-spotlight': {
    title: 'Future Self Spotlight',
    greeting: "The Future Self Spotlight surfaces your vision to keep you motivated and on track.",
    hints: "• Review your Spotlight Card to reconnect with your core vision statement.\n• Respond to the Reflection Prompt to check how aligned today's actions are with your future self.\n• Scroll the Progress Timeline to see how your vision has evolved since you began.\nIf you're unsure, just re-reading your identity statement is enough - small moments of reconnection add up."
  },
  '/vault': {
    title: 'The Vault',
    greeting: "The Vault is your complete wellness archive - every log, insight, and entry you've ever made, all in one place.",
    hints: "• Use the Category Tabs (Mood, Sleep, Nutrition, Journal, etc.) to browse specific data types.\n• Tap the Search bar to find a specific entry by date, keyword, or tag.\n• Use the Export button to download your data in CSV format for personal records or sharing with a clinician.\nIf you're unsure where something is, use the search - the Vault indexes everything you've ever logged."
  },
  '/crisis': {
    title: 'Crisis Support',
    greeting: "You're not alone. Crisis Support is here to provide immediate help when you need it most.",
    hints: "• Tap Emergency Contacts at the top to quickly call or text a trusted person in your safety network.\n• Use the Grounding Exercises (5-4-3-2-1 sensory technique is listed first) to anchor yourself right now.\n• Find Crisis Hotlines for your region - all numbers are listed and tap-to-call enabled.\nIf you're unsure what to do, start with the breathing exercise at the top - 4 counts in, hold 4, out 4."
  },
  '/connect-and-grow': {
    title: 'Connect & Grow',
    greeting: "Connect & Grow is your community space for shared challenges, accountability, and mutual support.",
    hints: "• Browse active Challenges and tap 'Join' on one that aligns with a goal you're working on.\n• Use the Accountability Partners section to pair up with someone for mutual check-ins.\n• Check the Leaderboard to see community progress and celebrate others' milestones.\nIf you're unsure where to start, joining a 7-day challenge is the lowest-commitment way to engage."
  },
  '/community': {
    title: 'Connected Collective',
    greeting: "Welcome to the Connected Collective - your community feed for connection, growth, and shared wins.",
    hints: "• Scroll the Feed to see recent posts from community members and react with support.\n• Tap Challenges to browse and join group activities aligned with your goals.\n• Use the Groups section to find or create a topic-specific community space.\nIf you're unsure whether to post, sharing a small win is always welcome - the community is supportive by design."
  },
  '/courses': {
    title: 'Wellness Courses',
    greeting: "Wellness Courses are structured learning journeys on mental health, resilience, and personal growth.",
    hints: "• Browse Course Cards and tap any to see the full curriculum before enrolling.\n• Use the Category Filters (Anxiety, Sleep, Relationships, Mindfulness) to find courses relevant to you.\n• Check the Progress Tracker on any enrolled course to see where you left off.\nIf you're unsure which course to start, tell me what you've been struggling with and I'll recommend one."
  },
  '/achievements': {
    title: 'Achievements',
    greeting: "Achievements celebrate every meaningful step you've taken on your wellness journey.",
    hints: "• Browse the Badge Gallery to see which milestones you've already unlocked and what's coming next.\n• Check the Points Counter at the top to see your total wellness score from consistent engagement.\n• Tap any locked badge to see exactly what action will unlock it - most are closer than you think.\nIf you're unsure what to do next, the 'Almost There' section highlights the badges nearest to unlocking."
  },
  '/my-scores': {
    title: 'My Scores',
    greeting: "My Scores gives you a transparent view of every wellness metric and what's influencing it right now.",
    hints: "• Tap your overall Wellness Score to expand the breakdown by category (mental, physical, recovery).\n• Scroll to Contributing Factors to see which logged behaviours are helping or hurting each score.\n• Use the History Chart to track how your scores have changed over the past 7 or 30 days.\nIf you're unsure why a score is low, ask me - I can explain what specific inputs are pulling it down."
  },
  '/all-tools': {
    title: 'All Tools',
    greeting: "All Tools gives you a bird's eye view of every feature in Therappi - discover what you haven't tried yet.",
    hints: "• Use the Category Filters (Mind, Body, Spirit, Connect) to browse tools by wellness domain.\n• Tap the Search bar to find a specific tool by name.\n• Look for the 'New' badges on tool cards to discover recently added features.\nIf you're unsure where to start, sort by Most Popular to see what other members find most valuable."
  },
  '/journal': {
    title: 'Journal',
    greeting: "Your Journal is a private, judgement-free space - writing is one of the most effective tools for emotional processing.",
    hints: "• Tap '+ New Entry' to open a blank page - write freely or use a Prompt for guided reflection.\n• Browse Past Entries by scrolling or using the Search bar to find entries by keyword or date.\n• Add Tags to your entries to group them by theme and spot patterns over time.\nIf you're unsure what to write, pick a prompt from the Prompts tab - they're designed to get you started in seconds."
  },
  '/exercise': {
    title: 'Exercise Tracker',
    greeting: "The Exercise Tracker helps you log movement and discover how physical activity affects your mental state.",
    hints: "• Tap 'Log Workout' and select an Activity Type (run, yoga, strength, walk) to record a session.\n• Set the Duration and Intensity for a more accurate mood-movement correlation over time.\n• Check the Mood Correlation chart to see which types of exercise lift your mood most.\nIf you're unsure what counts, even a 10-minute walk is worth logging - consistency matters more than intensity."
  },
  '/goals': {
    title: 'Goals & Habits',
    greeting: "Goals & Habits is where intentions become actions - set a goal, build the habit, and watch it compound.",
    hints: "• Tap 'Add Goal' to define a new intention with a measurable outcome and target date.\n• Check your Habit Streaks each day - tapping the check mark maintains your chain.\n• Review Progress Bars on each Goal Card to see how close you are to your target.\nIf you're unsure what goal to set, start with one small daily habit - just one is enough to build momentum."
  },
  '/manifestation': {
    title: 'Manifestation',
    greeting: "Manifestation combines vision, intention, and consistent attention to help you create the life you want.",
    hints: "• Add images, words, or colours to your Vision Board by tapping the + icon in any section.\n• Write and save Affirmations that you can review as a daily ritual to reinforce your intentions.\n• Use the Intention Setting section to write a specific goal with a feeling and a timeframe.\nIf you're unsure how to use the vision board, just start with one image or word that represents how you want to feel."
  },
  '/relational': {
    title: 'Relational Wellness',
    greeting: "Relational Wellness helps you invest in the connections that most influence your health and happiness.",
    hints: "• Use the Relationship Map to identify your key relationships and rate their current health.\n• Answer the Check-In Prompts to reflect on a specific relationship and what it needs right now.\n• Explore Communication Tools for scripts and techniques to navigate difficult conversations.\nIf you're unsure where to start, pick the relationship in your life that feels most strained - that's usually the one needing attention."
  },
  '/thought-tracker': {
    title: 'Thought Tracker',
    greeting: "The Thought Tracker applies CBT techniques to help you catch, examine, and reframe unhelpful thinking.",
    hints: "• Tap 'Add Thought' to log a negative or intrusive thought - describe exactly what went through your mind.\n• Use the Cognitive Distortions checklist to identify which pattern is at play (catastrophising, all-or-nothing, etc.).\n• Complete the Reframe Tool to generate a more balanced, evidence-based alternative thought.\nIf you're unsure what counts as a thought to log, anything that caused a strong emotional reaction is worth examining."
  },
  '/mental-tools': {
    title: 'Mental Tools Hub',
    greeting: "The Mental Tools Hub brings together evidence-based psychological techniques from CBT, DBT, and ACT.",
    hints: "• Browse Tool Categories and tap any card to open a guided exercise with step-by-step instructions.\n• Filter by Approach (CBT, DBT, ACT, Mindfulness) to find techniques aligned with your preferred style.\n• Try the Recommended For You section at the top - tools are personalised based on your recent check-in data.\nIf you're unsure which approach to use, CBT tools are a great default for managing thoughts and anxiety."
  },
  '/trends': {
    title: 'Trends',
    greeting: "Trends transforms your daily logs into visual patterns so you can understand what's really happening over time.",
    hints: "• Use the Time Range Selector (7d, 30d, 90d) to zoom in or out on your data.\n• Toggle individual Metric Lines on and off to compare specific dimensions like sleep and stress together.\n• Scroll to the Correlations section to see which pairs of metrics move together in your personal data.\nIf you're unsure what a pattern means, ask me - I can interpret any trend chart in plain language."
  },
  '/historical-data': {
    title: 'Historical Data',
    greeting: "Historical Data is your complete data archive - every entry you've ever made, searchable and explorable.",
    hints: "• Use the Date Range Picker to focus on a specific period (e.g. last month or a challenging season).\n• Switch between Data Categories (mood, sleep, nutrition, exercise) using the tab bar.\n• Tap the Export button to download your data as a CSV for personal records or to share with a healthcare provider.\nIf you're unsure what you're looking for, the Mood category is a good starting point for spotting life patterns."
  },
  '/account': {
    title: 'Account',
    greeting: "Manage your account details, preferences, and privacy settings all in one place.",
    hints: "• Update your Profile Info (name, photo, bio) in the Profile section at the top.\n• Change your Email or Password in the Security section - we recommend enabling two-factor auth.\n• Review your Privacy Preferences to control what data is retained and for how long.\nIf you're unsure about any setting, ask me before changing it - some settings affect your data history."
  },
  '/subscription': {
    title: 'Subscription',
    greeting: "View and manage your Therappi subscription - see what's included in your current plan.",
    hints: "• Check your Current Plan card to see your plan name, renewal date, and included features.\n• Tap 'Explore Plans' to compare tiers and see what unlocks with an upgrade.\n• View your Billing History below the plan card for past invoices and payment details.\nIf you're unsure whether to upgrade, ask me what features are exclusive to higher tiers - I'll give you an honest breakdown."
  },
  '/credits': {
    title: 'Credits',
    greeting: "Credits power certain AI features - track your balance and usage to make the most of them.",
    hints: "• Check your Credit Balance at the top of the page - it refreshes with your billing cycle.\n• Scroll to Usage History to see which features consumed credits and when.\n• Tap 'Top Up' if you're running low and want to continue using AI-intensive features uninterrupted.\nIf you're unsure how credits are spent, ask me - I can explain which features use them and by how much."
  },
  '/clinician': {
    title: 'Clinician Portal',
    greeting: "The Clinician Portal gives you a structured, data-rich view of your clients to enhance the care you provide.",
    hints: "• Open the Client List and tap any client to access their shared wellness data and session history.\n• Use Session Notes to document key themes, interventions, and follow-up actions after each appointment.\n• Check Risk Alerts at the top - these surface automatically when a client's data shows concerning patterns.\nIf you're unsure how to interpret a client's data, the AI Summary tab provides a plain-language overview."
  },
  '/client-portal': {
    title: 'Therapist Connection',
    greeting: "The Therapist Connection lets you see exactly what data you're sharing with your clinician and control it.",
    hints: "• Review Shared Data categories to confirm which parts of your Therappi data your clinician can see.\n• Use Consent Settings to grant or revoke access to specific data types at any time.\n• Check the Messages section to communicate directly with your clinician between sessions.\nIf you're unsure what to share, you can start with mood and sleep data only - you're always in control."
  },
  '/rock-remedies': {
    title: 'Rock Remedies',
    greeting: "Rock Remedies explores Bach Flower Remedies and crystal healing as complementary wellness tools.",
    hints: "• Use the Remedy Finder by describing how you're feeling - it will suggest relevant Bach Flower Remedies.\n• Browse the Crystal Guide to explore properties and uses of different healing crystals.\n• Tap 'Get Personalised Recommendations' for a curated selection based on your current wellness data.\nIf you're unsure about the science, these are complementary tools - they work best alongside your other Therappi practices."
  },
  '/meditation-library': {
    title: 'Guided Meditations',
    greeting: "The Meditation Library is your full collection of guided sessions - find the perfect one for right now.",
    hints: "• Type in the Search bar to find meditations by keyword (e.g. 'anxiety', 'sleep', 'confidence').\n• Use the Category, Level, and Duration filters to narrow down to sessions that fit your time and experience.\n• Tap any card to preview, then hit Play - you can return to where you left off anytime.\nIf you're unsure which to pick, tell me how you're feeling and I'll recommend a specific session."
  },
  '/telempathy-tools': {
    title: 'Telempathy Tools',
    greeting: "Telempathy Tools support the development of your intuitive, empathic, and energetic awareness.",
    hints: "• Browse the Tool Cards and tap any to open a guided practice - from aura reading to energy clearing.\n• Use the Practice Exercises section to work through structured exercises at your own pace.\n• Check your Progress Tracker to see how consistently you've been engaging with these practices.\nIf you're unsure where to begin, the Intuition Warm-Up exercise is the recommended starting point for newcomers."
  },
  '/notifications': {
    title: 'Notifications',
    greeting: "Your notification history - see all recent alerts, reminders, and updates from Therappi.",
    hints: "• Scroll through the Notification List to catch up on anything you missed.\n• Use the Read/Unread filter to quickly surface new notifications.\n• Go to Settings > Notifications if you want to change which alerts you receive and when.\nIf you're unsure why you received a notification, tap it - most link directly to the relevant feature."
  },
  '/mind-body-insights': {
    title: 'Mind-Body Insights',
    greeting: "Mind-Body Insights reveals the deep connections between your physical health and mental wellbeing.",
    hints: "• Explore the Correlation Charts to see how your sleep quality correlates with next-day mood or energy.\n• Interact with the Body-Mind Map to tap specific areas and see related logged metrics.\n• Browse the Insight Cards for AI-generated observations specific to your data patterns.\nIf you're unsure what a correlation means, ask me - I can translate the data into practical actions."
  },
  '/course-library': {
    title: 'Course Library',
    greeting: "The Course Library gives you access to our full range of structured wellness learning programmes.",
    hints: "• Use the Search bar to find a course by topic, instructor, or keyword.\n• Apply Category Filters (Anxiety, Sleep, Grief, Relationships) to narrow down relevant options.\n• Tap any Course Card to read the full curriculum overview before committing to enrol.\nIf you're unsure which course suits you, tell me what you want to improve and I'll point you to the right one."
  },
  '/garmin-demo': {
    title: 'Garmin Data',
    greeting: "View your Garmin fitness data in context - see how activity, heart rate, and sleep connect to your mental wellness.",
    hints: "• Check today's Activity summary at the top - steps, active minutes, and intensity breakdown.\n• Scroll to the Heart Rate section to see resting HR trends and stress score from your device.\n• Review the Sleep Data card to compare Garmin's sleep staging with how you felt when you woke up.\nIf you're unsure how to interpret your Garmin data, ask me - I can explain what any metric means for your wellness."
  },
};

// Prefix groups for routes not in the exact map
const PREFIX_GROUPS = [
  '/challenge/', '/protocol/', '/longevity/protocol/', '/vault/',
  '/clinician/', '/course/', '/wellness-tracks/', '/telempathy/',
];

export const getPageContext = (pathname: string): { key: string; context: PageContext } => {
  // 1. Exact match
  if (PAGE_CONTEXT_MAP[pathname]) {
    return { key: pathname, context: PAGE_CONTEXT_MAP[pathname] };
  }
  // 2. Prefix match
  for (const prefix of PREFIX_GROUPS) {
    if (pathname.startsWith(prefix)) {
      const baseKey = prefix.endsWith('/') ? prefix.slice(0, -1) : prefix;
      if (PAGE_CONTEXT_MAP[baseKey]) {
        return { key: baseKey, context: PAGE_CONTEXT_MAP[baseKey] };
      }
    }
  }
  // 3. Try parent path (e.g. /longevity/protocol/x → /longevity)
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 1) {
    const parent = '/' + segments[0];
    if (PAGE_CONTEXT_MAP[parent]) {
      return { key: parent, context: PAGE_CONTEXT_MAP[parent] };
    }
  }
  // 4. Fallback
  const fallback: PageContext = {
    title: pathname.slice(1).split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Current Page',
    greeting: `You're currently on ${pathname}. Tell me what you're trying to do here and I'll guide you.`,
    hints: ''
  };
  return { key: '(none)', context: fallback };
};

const getContextualGreeting = (pathname: string): string => {
  const { context: ctx } = getPageContext(pathname);
  if (ctx.hints) {
    return `${ctx.greeting}\n\n${ctx.hints}\n\nAsk me anything about what you see here!`;
  }
  return ctx.greeting;
};

// Routes where the guide should NOT appear (public/auth routes only)
const PUBLIC_ROUTES = ['/auth', '/reset-password', '/terms', '/privacy', '/beta', '/admin', '/admin-portal', '/onboarding', '/legal/terms', '/legal/privacy', '/subprocessors', '/ai-transparency', '/consumer-health-notice', '/subscribe', '/clinician-sales', '/for-therapists', '/elevenlabs-grant', '/google-play-accelerator', '/whoop-login', '/garmin-login', '/accept-invitation'];

// Fetch WHOOP data for context
const fetchWhoopContext = async (): Promise<WhoopContextData | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    const startStr = startDate.toISOString().split('T')[0];

    const { data: whoopData } = await (supabase as any)
      .from('whoop_data')
      .select('date, recovery_score, hrv_score, sleep_performance, strain_score')
      .eq('user_id', user.id)
      .gte('date', startStr)
      .order('date', { ascending: false })
      .limit(7);

    if (!whoopData || whoopData.length === 0) return null;

    const today = whoopData[0];
    const validRecovery = whoopData.filter((d: any) => d.recovery_score != null);
    const validHrv = whoopData.filter((d: any) => d.hrv_score != null);
    const validSleep = whoopData.filter((d: any) => d.sleep_performance != null);

    // Calculate trend
    let trend: 'improving' | 'declining' | 'stable' | null = null;
    if (validRecovery.length >= 4) {
      const recentAvg = validRecovery.slice(0, 2).reduce((sum: number, d: any) => sum + d.recovery_score, 0) / 2;
      const olderAvg = validRecovery.slice(-2).reduce((sum: number, d: any) => sum + d.recovery_score, 0) / 2;
      const diff = recentAvg - olderAvg;
      if (diff > 5) trend = 'improving';
      else if (diff < -5) trend = 'declining';
      else trend = 'stable';
    }

    return {
      todayRecovery: today?.recovery_score ?? null,
      todayHrv: today?.hrv_score ?? null,
      todaySleep: today?.sleep_performance ?? null,
      todayStrain: today?.strain_score ?? null,
      avgRecovery: validRecovery.length > 0 ? validRecovery.reduce((sum: number, d: any) => sum + d.recovery_score, 0) / validRecovery.length : null,
      avgHrv: validHrv.length > 0 ? validHrv.reduce((sum: number, d: any) => sum + d.hrv_score, 0) / validHrv.length : null,
      avgSleep: validSleep.length > 0 ? validSleep.reduce((sum: number, d: any) => sum + d.sleep_performance, 0) / validSleep.length : null,
      trend,
      recentDays: whoopData.map((d: any) => ({
        date: d.date,
        recovery: d.recovery_score,
        hrv: d.hrv_score,
        sleep: d.sleep_performance,
        strain: d.strain_score
      }))
    };
  } catch (error) {
    return null;
  }
};

// Fetch daily check-in data for context
const fetchCheckinContext = async (): Promise<DailyCheckinContext | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const today = new Date().toISOString().split('T')[0];
    
    const { data: checkin } = await supabase
      .from('daily_checkins')
      .select('emotion, sleep_quality, body_feeling, cravings_level')
      .eq('user_id', user.id)
      .eq('date', today)
      .maybeSingle();

    if (!checkin) return null;

    return {
      emotion: checkin.emotion,
      sleepQuality: checkin.sleep_quality,
      energyLevel: checkin.body_feeling,
      stressLevel: checkin.cravings_level
    };
  } catch (error) {
    return null;
  }
};

export const FloatingGuideButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);
  const isWhoopPage = location.pathname === '/whoop-dashboard';

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Reset greeting when route changes so the guide is always page-aware
  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== lastPathRef.current) {
      lastPathRef.current = location.pathname;
      setHasGreeted(false);

      const { key, context } = getPageContext(location.pathname);
      if (import.meta.env.DEV) {
        console.log('[TherappiGuide] route changed', { pathname: location.pathname, matchedKey: key, matchedTitle: context.title, hasGreeted: false, isOpen });
      }

      // If guide is already open, show the new page greeting immediately
      if (isOpen) {
        const greeting = getContextualGreeting(location.pathname);
        setMessages([{
          id: Date.now().toString(),
          content: greeting,
          role: 'assistant',
          isNew: true
        }]);
        setHasGreeted(true);
      }
    }
  }, [location.pathname, isOpen]);

  const handleOpen = () => {
    setIsOpen(true);
    
    // Play pop sound when guide opens
    try {
      const popSound = new Audio('/sounds/therappi-guide-pop.wav');
      popSound.volume = 0.5;
      popSound.play().catch(() => {});
    } catch {}
    
    // Always greet fresh on open using current route context
    setHasGreeted(false);
    const { key, context } = getPageContext(location.pathname);
    const greeting = getContextualGreeting(location.pathname);

    if (import.meta.env.DEV) {
      console.log('[TherappiGuide] opened', { pathname: location.pathname, matchedKey: key, matchedTitle: context.title, hasGreeted: false, isOpen: true });
    }

    setMessages([{
      id: Date.now().toString(),
      content: greeting,
      role: 'assistant',
      isNew: true
    }]);
    setHasGreeted(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setHasGreeted(false); // Reset so next open gets fresh greeting
  };

  const handleGoToTherappi = () => {
    setIsOpen(false);
    navigate('/therapy');
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue,
      role: 'user'
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Prepare messages for API
      const apiMessages = [...messages, userMessage].map(m => ({
        role: m.role,
        content: m.content
      }));

      // Fetch contextual data for WHOOP pages
      let whoopContext: WhoopContextData | null = null;
      let checkinContext: DailyCheckinContext | null = null;
      
      if (isWhoopPage) {
        [whoopContext, checkinContext] = await Promise.all([
          fetchWhoopContext(),
          fetchCheckinContext()
        ]);
      }

      const { context: pageCtx } = getPageContext(location.pathname);
      const payload = {
        messages: apiMessages,
        currentPage: location.pathname,
        currentPageContext: pageCtx,
        whoopContext,
        checkinContext
      };

      if (import.meta.env.DEV) {
        console.log('[TherappiGuide] request payload keys:', Object.keys(payload), 'pathname:', location.pathname);
      }

      const response = await fetch(
        `https://jbdsdzmyqkfbqijnoqmo.supabase.co/functions/v1/guide-chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        if (response.status === 429) {
          toast.error("Too many requests. Please wait a moment.");
        } else if (response.status === 402) {
          toast.error("Service temporarily unavailable.");
        }
        throw new Error(`HTTP ${response.status}`);
      }

      if (!response.body) throw new Error("No response body");

      // Stream the response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = "";
      const assistantId = (Date.now() + 1).toString();

      // Add empty assistant message that we'll update
      setMessages(prev => [...prev, { id: assistantId, content: "", role: 'assistant' }]);

      const sseBuf = createSSELineBuffer();
      
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) {
          const final = flushAndProcessSSE(sseBuf);
          assistantContent += final.texts.join('');
          break;
        }
        
        const { texts, done: isDone } = processSSEChunk(sseBuf, decoder.decode(value, { stream: true }));
        if (texts.length > 0) {
          assistantContent += texts.join('');
          const showButton = assistantContent.includes('[SHOW_THERAPPI_BUTTON]');
          const cleanContent = assistantContent.replace(/\[SHOW_THERAPPI_BUTTON\]/g, '').trim();
          setMessages(prev => 
            prev.map(m => m.id === assistantId ? { ...m, content: cleanContent, showTherappiButton: showButton } : m)
          );
        }
        if (isDone) { streamDone = true; break; }
      }

      // Final check for button marker and extract navigation links after stream completes
      const showButton = assistantContent.includes('[SHOW_THERAPPI_BUTTON]');
      const cleanContent = assistantContent.replace(/\[SHOW_THERAPPI_BUTTON\]/g, '').trim();
      const { cleanText, links } = extractNavigationLinks(cleanContent);
      setMessages(prev => 
        prev.map(m => m.id === assistantId ? { ...m, content: cleanText, showTherappiButton: showButton, navigationLinks: links } : m)
      );

    } catch (error) {
      // Fallback response with typewriter effect
      const fallbackMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment, or explore the app - you're doing wonderfully by taking this step for your wellbeing.",
        role: 'assistant',
        isNew: true // Enable typewriter for fallback message
      };
      setMessages(prev => {
        // Remove empty assistant message if it exists, add fallback
        const filtered = prev.filter(m => m.content !== "");
        return [...filtered, fallbackMessage];
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Don't render on public routes
  if (isPublicRoute) return null;

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleOpen}
            data-tour="guide-button"
            className={cn(
              "fixed bottom-24 right-4 z-50",
              "w-14 h-14 rounded-full",
              "bg-primary text-primary-foreground",
              "shadow-lg shadow-primary/25",
              "flex items-center justify-center",
              "transition-colors hover:bg-primary/90"
            )}
            aria-label="Open guide"
          >
            <Sparkles className="w-6 h-6" />
            
            {/* Pulse ring */}
            <span className="absolute inset-0 rounded-full bg-primary/30 animate-ping" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={cn(
              "fixed bottom-24 right-4 z-50",
              "w-[calc(100vw-2rem)] max-w-sm",
              "h-[400px] max-h-[60vh]",
              "bg-background border border-border",
              "rounded-2xl shadow-xl",
              "flex flex-col overflow-hidden"
            )}
          >
            {/* Header */}
            <div className="border-b border-border bg-muted/30">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Therappi Guide</p>
                    <p className="text-xs text-muted-foreground">Always here to help</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClose}
                  className="h-8 w-8"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              {import.meta.env.DEV && (
                <div className="px-4 pb-2">
                  <p className="text-[10px] font-mono text-muted-foreground bg-muted rounded px-2 py-0.5 truncate">
                    path={location.pathname} | match={getPageContext(location.pathname).key} | greeted={String(hasGreeted)}
                  </p>
                </div>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((message, index) => (
                <div key={message.id}>
                  <ChatBubble
                    isUser={message.role === 'user'}
                    animate={true}
                  >
                    {message.role === 'assistant' && message.isNew ? (
                      <TypewriterMessage 
                        text={message.content}
                        speed={15}
                        showCursor={true}
                        className="text-sm whitespace-pre-wrap"
                        onComplete={() => {
                          // Mark as no longer new after typing completes
                          setMessages(prev => 
                            prev.map(m => m.id === message.id ? { ...m, isNew: false } : m)
                          );
                        }}
                      />
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    )}
                  </ChatBubble>
                  
                  {/* Navigation buttons from extracted links */}
                  {message.navigationLinks && message.navigationLinks.length > 0 && (
                    <div className="mt-2 ml-2 flex flex-wrap gap-2">
                      {message.navigationLinks.map((link, index) => (
                        <Button
                          key={`${link.path}-${index}`}
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setIsOpen(false);
                            navigate(link.path);
                          }}
                          className="gap-1 text-xs bg-primary/10 hover:bg-primary/20 border-primary/30"
                        >
                          {link.label}
                          <ArrowRight className="w-3 h-3" />
                        </Button>
                      ))}
                    </div>
                  )}
                  
                  {message.showTherappiButton && (
                    <div className="mt-2 ml-2">
                      <Button
                        size="sm"
                        onClick={handleGoToTherappi}
                        className="gap-2"
                      >
                        <MessageCircle className="w-4 h-4" />
                        Go to Therappi AI
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {isLoading && messages[messages.length - 1]?.content === "" && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Thinking...</span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-border bg-muted/20">
              <div className="flex gap-2">
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me anything..."
                  className="flex-1 text-sm"
                  disabled={isLoading}
                />
                <Button
                  size="icon"
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isLoading}
                  className="shrink-0"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
